// Per-asset upload orchestrator. Mirrors vanilla submitVersionUpload
// (legacy/index.html lines 9862-10038). Pure function — caller (TTSView /
// STSView) supplies the snapshot context and pre-built ExportInput so the
// orchestrator stays React/store-agnostic and unit-testable.
//
// Pipeline:
//   1. Collect every fav-bearing block for the active asset
//   2. Render the Voice-Only WAV via renderVoiceOnlyWav(exportInput)
//   3. Optionally render the PreMixed WAV (when video audio or SFX bed)
//   4. Build script.txt + params.txt + optional timecodes.csv
//   5. Optionally include the active video Blob
//   6. Dispatch all files via api.uploadAsset() — last file still carries
//      isFinal:true (vanilla wire format); the server commits the version
//      record after all PUTs (server/uploadVersions.ts)
//   7. Return UploadResultRecord for the caller to append to the store
//
// Progress is reported via `onProgress(event)` where `event` is a
// `UploadProgressEvent` discriminated union. Callers (UploadModal,
// UploadSummary) use the `kind` to render a per-file checklist + an overall
// progress bar.

import type { Block } from '@shared/types';
import { VIDEO_EXPORT_STREAM_THRESHOLD } from '@shared/constants';
import {
  uploadAsset,
  uploadLargeFile,
  cancelUploadVersion,
  fetchVersions,
  LARGE_FILE_THRESHOLD,
  type UploadFilePayload,
} from './api';
import {
  audioBufferToWav,
  renderMixedAudioBuffer,
  renderVoiceOnlyWav,
  type ExportInput,
} from './audioExport';
import {
  exportTimelineVideoToBlob,
  hasVideoExportSupport,
  type VideoExportInput,
} from './videoExport';
import {
  buildBlockFilename,
  buildStsBlockFilename,
  buildAssetFolderName,
  buildFilePrefix,
} from './uploadFilenames';
import {
  buildScriptTxt,
  buildParamsTxt,
  buildTimecodesCsv,
  buildOriginalVideoPathTxt,
  timecodesCsvBlob,
} from './uploadDocs';
import { voiceTrackIndex } from './voiceMapping';
import { BUNDLE_SUM_LIMIT, splitBundle } from './uploadSplit';
import { createProgress, prepAt, progressBytes, progressRatio, progressStage, type ProgressStage } from './uploadProgress';
import { resolveBlockVoiceId, resolveTakeVoiceId } from './voiceMigration';
import { debugLog } from './debugLog';

/** Discriminated union emitted by the upload pipeline. UploadModal /
 *  UploadSummary subscribe and render a per-file checklist plus an overall
 *  progress bar.
 *
 *  - `message`            free-form stage label without a specific file (also
 *                         drives the modal's bottom status line)
 *  - `overall`            overall ratio 0..1 + optional label (drives the top
 *                         bar)
 *  - `file-start`         a file enters "preparing" state
 *  - `file-bytes`         bytes-sent update for a file (used during the
 *                         multipart POST; we emit a synthetic "Upload" file)
 *  - `server-processing`  XHR has flushed all request-body bytes to the OS
 *                         socket buffer; we're now waiting on the server to
 *                         finish its WebDAV PUTs and respond. Emitted exactly
 *                         once per upload — the modal swaps the byte-counter
 *                         for "Processing on server…" and switches the bar to
 *                         indeterminate. Without this signal the bar would
 *                         claim 100% while the server is still working,
 *                         because XHR upload-progress reports OS-buffer
 *                         flushes, not server-side completion.
 *  - `file-done`          file is fully prepared / uploaded
 *  - `file-error`         file failed (currently unused — POST is all-or-
 *                         nothing, kept for future per-file fetch pipeline)
 */
/** Discrete pipeline phase. Drives the UploadModal's "what's happening?"
 *  label and decides whether the progress bar can be shown as determinate
 *  (real percentage) or must fall back to indeterminate (sliding stripe).
 *
 *  Determinate is only honest when:
 *    - phase === 'muxing-video' AND we receive a numeric percent from MP4Box
 *    - phase === 'uploading' AND total bytes > BYTES_DETERMINATE_THRESHOLD
 *  Otherwise: sliding stripe + the phase label tells the user what's running. */
export type UploadPhase =
  | 'collecting'
  | 'rendering-voice'
  | 'rendering-mixed'
  | 'muxing-video'
  | 'uploading'
  | 'uploading-large-file'
  | 'server'
  | 'done';

export type UploadProgressEvent =
  | { kind: 'message'; text: string }
  /** Echter, bytegewichteter Gesamtfortschritt (`src/lib/uploadProgress.ts`):
   *  `ratio` 0 … 1 monoton, `stage` fürs Label, `bytes` für „123 / 180 MB“
   *  (total 0, solange die Dateien noch gebaut werden). */
  | { kind: 'overall'; ratio: number; stage: ProgressStage; bytes: { sent: number; total: number }; text?: string }
  | { kind: 'file-start'; name: string; sizeBytes?: number }
  | { kind: 'file-bytes'; name: string; sentBytes: number; totalBytes?: number }
  | { kind: 'server-processing'; name: string }
  | { kind: 'file-done'; name: string }
  | { kind: 'file-error'; name: string; error: string }
  /** Nicht-fataler Hinweis für das Talent — der Upload läuft weiter, aber
   *  etwas ist anders als erwartet (bisher: der Video-Mux ist gescheitert).
   *  Das Modal sammelt die Texte und zeigt sie in der Abschluss-Ansicht
   *  (Spec 2026-09-21 § 7). */
  | { kind: 'warning'; text: string }
  | { kind: 'phase'; phase: UploadPhase; label: string }
  | { kind: 'mux-progress'; percent: number }
  /** Emitted ONCE at the start of submitAssetUpload with the total
   *  number of `phase` events the modal will see (excluding 'done',
   *  which fires after the checkmark view takes over). Lets the modal
   *  show "2/6" step counters so the user knows how many steps remain. */
  | { kind: 'phase-plan'; total: number };

export interface UploadCtx {
  toolId: string;
  /** Resolved video source URL — same value the download path uses
   *  (videoLocalSrc ?? videoRemoteSrc). Mirrors the download flow so the
   *  mux pipeline runs identically. Null when no video is loaded. */
  videoSrc: string | null;
  sessionFolder: string;
  langCode: string;
  shortId: string;
  userName: string;
  /** GLOBALE 0-basierte Asset-Nummer für Ordner- und Dateinamen
   *  (`assetGlobalIdx(asset, combinedIdx)`, Audit 2026-09-24 H4) — in
   *  Multi-Lingual-Projekten die Position in der ungefilterten Liste, nicht
   *  der Index in der Sprach-Config. Wird nur für Namen benutzt, nie als
   *  Store-Schlüssel. */
  assetIdx: number;
  asset: {
    id: string;
    title: string;
    assetType: 'tts' | 'sts' | 'free';
    script: string;
    isCustom: boolean;
    /** Allowed voices for this asset (Multi-Voice #173). Length ≥2 →
     *  per-block-filenames bekommen ein _VoiceN-Suffix. Single-Voice
     *  (length===1) bleibt byte-identisch zum pre-Phase-9-Format. */
    voiceIds: string[];
    /** Admin-vorgegebenes Cloud-Video (URL-kodiert, relativ zu `Kunden/`).
     *  Gesetzt → `_ORIGINAL_VIDEO_PATH.txt` in jeder Version. */
    videoPath?: string | null;
  };
  voiceName: string;
  /** Block array for the asset — used for filename/script/params/csv builders. */
  blocks: Block[];
  videoDuration: number;
  /** Pre-built ExportInput so the orchestrator doesn't need store access. */
  exportInput: ExportInput;
  freeMode?: 'tts' | 'sts';
  /** Next version number — caller computes from session.versions[asset.id]. */
  nextVersion: number;
  includeVideo?: boolean;
  videoBlob?: Blob | null;
  /** Größe des Admin-Cloud-Videos in Bytes (HEAD via `videoInputSize`).
   *  Entscheidet, ob das Premix-MP4 automatisch mitgeht (Spec 2026-09-21
   *  § 7). `null` = Größe unbekannt (HEAD gescheitert) → wie > 500 MB nur
   *  mit `includeVideo` (Integrations-Review 2026-09-24); 0 / undefined =
   *  kein Cloud-Video bzw. nicht angegeben. */
  cloudVideoSize?: number | null;
  note?: string | null;
  /** When true, params.txt uses the STS-shaped lines. Defaults to false (TTS). */
  isSts?: boolean;
  onProgress?(event: UploadProgressEvent): void;
  /** Aborts all in-flight upload XHRs when fired. UploadModal owns the
   *  AbortController; X-button-confirm-cancel calls .abort() and the
   *  pipeline rejects with AbortError. */
  signal?: AbortSignal;
  /** Meldet, unter welcher Version und in welchem Ordner dieser Upload
   *  tatsächlich läuft — zuerst nach dem Server-Resync, dann noch einmal
   *  mit der vom Server vergebenen Nummer nach dem Bundle-POST. Der
   *  Aufrufer räumt bei Abbruch GENAU diese Version auf; die lokale
   *  `nextVersion` kann davon abweichen (zweiter Tab, verlorene Antwort
   *  eines früheren Uploads) — mit ihr löschte der Cancel früher eine
   *  fertige Version (Audit M15). */
  onUploadTarget?(target: UploadTarget): void;
}

export interface UploadTarget {
  version: number;
  assetFolderName: string;
  /** Marke der Server-Reservierung — erst nach der Antwort des Bundle-POST
   *  bekannt. Mit ihr darf der Cancel auch die eigene, schon vollständige
   *  Version entfernen (Abbruch nach dem letzten Netzwerkschritt); ohne sie
   *  entfernt der Dienst nur halbe Versionen (Integrations-Review 2026-09-24). */
  uploadId?: string;
}

export interface UploadResultRecord {
  version: number;
  files: string[];
  note: string | null;
  duration: number;
  uploadedAt: string;
  /** Sub-mode marker — mirrors `UploadVersion.mode`. Only set when the
   *  upload originated from a freeMode-bearing asset (custom + predefined
   *  free-choice); undefined for predefined TTS-only / STS-only assets. */
  mode?: 'tts' | 'sts';
}

/** Synthetic file-name used for the multipart POST byte-counter row. The
 *  modal renders this as a single "Upload" line with MB counter; we don't
 *  emit per-file events during the POST since the server doesn't break the
 *  multipart payload back out into discrete files. */
const UPLOAD_BUCKET = 'Upload to TRO Cloud';

/** Cloud-Videos bis zu dieser Größe gehen bei jedem Upload automatisch als
 *  Premix-MP4 mit; darüber entscheidet die (vorbelegte) Checkbox, weil jede
 *  Version das komplette Video erneut hochlädt (Spec 2026-09-21 § 7).
 *  Unbekannte Größe (`null`) zählt wie „zu groß": früher lieferte ein
 *  gescheiterter HEAD 0, und ein großes Video ging ungefragt mit
 *  (Integrations-Review 2026-09-24). */
export function shouldAutoUploadCloudVideo(sizeBytes: number | null): boolean {
  if (sizeBytes === null) return false;
  return sizeBytes <= VIDEO_EXPORT_STREAM_THRESHOLD;
}

/** Ordner der höchsten Version mit gemerktem `folder` (Server-Record), sonst null. */
export function latestRecordedFolder(versions: { version: number; folder?: string }[]): string | null {
  let best: { version: number; folder: string } | null = null;
  for (const v of versions) {
    if (typeof v.folder === 'string' && v.folder && (!best || Number(v.version) > best.version)) {
      best = { version: Number(v.version), folder: v.folder };
    }
  }
  return best?.folder ?? null;
}

/** 0-basierte Asset-Nummer aus einem Ordnernamen `N - …` (buildAssetFolderName), sonst null. */
export function folderNumber(folder: string): number | null {
  const m = /^(\d+) - /.exec(folder);
  const n = m ? Number(m[1]) : NaN;
  return Number.isInteger(n) && n >= 1 ? n - 1 : null;
}

export async function submitAssetUpload(
  ctx: UploadCtx,
): Promise<UploadResultRecord> {
  const emit = (e: UploadProgressEvent) => ctx.onProgress?.(e);

  const ready = ctx.blocks
    .map((block, blockIdx) => ({ block, blockIdx }))
    .filter(({ block }) => block.favIdx >= 0 && !!block.takes[block.favIdx]);
  if (ready.length === 0) throw new Error('No blocks ready for upload');

  // Inject the upload-cancel signal into the ExportInput passed down to the
  // audio renderers. Caller (TTSView/STSView) builds the snapshot once and
  // doesn't know about the signal — we add it here so AbortError propagates
  // cleanly out of OfflineAudioContext + WebCodecs at the next await
  // boundary, instead of wasting CPU on a render the user already cancelled.
  const exportInputWithSignal = ctx.signal
    ? { ...ctx.exportInput, signal: ctx.signal }
    : ctx.exportInput;
  const checkAbort = () => ctx.signal?.throwIfAborted();

  // Resync the version number against the server BEFORE building filenames.
  // `ctx.nextVersion` is derived from the locally-hydrated versions store,
  // which goes stale the moment an earlier upload wrote its KV record but the
  // client never recorded it (a failed upload after the server commit, or a
  // second tab). The server computes the authoritative version as max(KV)+1,
  // so a stale client would name files `_v{stale}` while the server filed them
  // under `Version {authoritative}/` — and the large-file phase + KV patch would
  // then target yet another folder/record. Re-reading KV here closes that gap;
  // on any fetch failure we fall back to the caller's number.
  let uploadVersion = ctx.nextVersion;
  // Ordner der bisherigen Versionen (Integrations-Review 2026-09-24): der
  // Server merkt sich je Version den tatsächlich verwendeten Asset-Ordner und
  // legt neue Versionen eines Assets in denselben — auch wenn der Admin
  // inzwischen Assets eingefügt/umsortiert hat und die aktuelle Nummer eine
  // andere ist. Der Client folgt dem schon hier, damit Dateipräfix
  // (`AssetN`) und die Pfade der großen Dateien zum Ordner passen.
  let recordedFolder: string | null = null;
  try {
    const serverVersions = await fetchVersions(ctx.toolId, ctx.sessionFolder);
    const forAsset = serverVersions[ctx.asset.id] ?? [];
    const serverMax = forAsset.reduce(
      (m, v) => Math.max(m, Number(v.version) || 0),
      0,
    );
    uploadVersion = serverMax + 1;
    recordedFolder = latestRecordedFolder(forAsset);
  } catch (err) {
    console.warn('[upload] version resync failed, using local nextVersion', err);
  }
  checkAbort();

  const filenameCtx = {
    langCode: ctx.langCode,
    shortId: ctx.shortId,
    // Im alten Ordner bleibt auch die Nummer im Dateinamen die alte.
    assetIdx: (recordedFolder ? folderNumber(recordedFolder) : null) ?? ctx.assetIdx,
    assetTitle: ctx.asset.title,
    version: uploadVersion,
  };
  const prefix = buildFilePrefix(filenameCtx);
  let assetFolderName = recordedFolder ?? buildAssetFolderName({
    assetIdx: ctx.assetIdx,
    title: ctx.asset.title,
    isCustom: ctx.asset.isCustom,
  });
  ctx.onUploadTarget?.({ version: uploadVersion, assetFolderName });

  // Multi-Voice (#173): assets with ≥2 voiceIds emit per-block filenames
  // with a `_VoiceN` suffix where N is the 1-based position of the block's
  // voiceId in `asset.voiceIds`. Single-Voice-Assets stay byte-identical
  // to the pre-Phase-9 format (voiceMarker stays undefined → no suffix).
  const isMultiVoice = ctx.asset.voiceIds.length >= 2;
  const computeVoiceMarker = (block: Block): number | undefined => {
    if (!isMultiVoice) return undefined;
    // Prefer the voice that actually generated the favorited take; fall
    // back through block.voiceId → asset.voiceIds[0]. Synthetic STS blocks
    // carry voiceId from the source take (see stsAssetToBlocks), so the
    // same code path works for both TTS and STS.
    const blockVoice = resolveBlockVoiceId(block, ctx.asset.voiceIds);
    const favTake = block.takes[block.favIdx];
    const actualVoice = favTake
      ? resolveTakeVoiceId(favTake, blockVoice)
      : blockVoice;
    const idx = voiceTrackIndex(actualVoice, ctx.asset.voiceIds);
    // Stale voiceId (idx === -1) → return undefined so the suffix is
    // omitted gracefully rather than emitting Voice0 / Voice-1.
    return idx >= 0 ? idx + 1 : undefined;
  };

  // STS uploads use Take{session}-{n} naming. TTS uploads use Block{n} via
  // buildBlockFilename. Synthetic STS blocks always carry the metadata
  // (populated by stsAssetToBlocks) — tool is pre-release so no legacy fallback.
  const makeBlockFilename = (block: Block, blockIdx: number): string => {
    const voiceMarker = computeVoiceMarker(block);
    if (ctx.isSts) {
      const meta = block._stsTakeMeta;
      if (meta?.session == null || meta?.takeInSession == null) {
        throw new Error(
          `STS block at index ${blockIdx} missing _stsTakeMeta — stsAssetToBlocks must populate session + takeInSession`,
        );
      }
      return buildStsBlockFilename({
        ...filenameCtx,
        session: meta.session,
        takeInSession: meta.takeInSession,
        voiceMarker,
      });
    }
    return buildBlockFilename({ ...filenameCtx, blockIdx, voiceMarker });
  };

  // ---- Phase plan ----
  // Zwei Makro-Stufen aus Sicht des Talents: „Preparing files“ (Sammeln /
  // Rendern / Mux) und „Uploading files“. Die feinen `phase`-Events bleiben
  // für Telemetrie; den Balken treibt `overall` aus dem Byte-Modell.
  emit({ kind: 'phase-plan', total: 2 });

  // ---- Echter Fortschritt (uploadProgress.ts) ----
  // Ein Zustand für den ganzen Lauf; jede Änderung geht als `overall` raus.
  const progress = createProgress();
  const emitOverall = (text?: string) => {
    emit({ kind: 'overall', ratio: progressRatio(progress), stage: progressStage(progress), bytes: progressBytes(progress), ...(text ? { text } : {}) });
  };
  const setPrep = (value: number, text?: string) => {
    progress.prep = Math.max(progress.prep, value);
    emitOverall(text);
  };

  // ---- Per-block audio payloads ----
  // Block files come from already-rendered takes — they're "ready" the moment
  // we read them out of the store, so we emit start+done back-to-back.
  //
  // STS deduplication: when the same Take is referenced by multiple
  // MasterClips (e.g. a take used 3× on the master timeline), the
  // synthBlocks array contains 3 entries that all map to the same source
  // file. Emitting 3 identical MP3s with the same filename would conflict
  // in the server → WebDAV PUT loop. Track filenames already emitted and
  // skip duplicates — the CSV (built later) still references each clip
  // separately so the studio editor sees each occurrence.
  emit({ kind: 'phase', phase: 'collecting', label: 'Collecting files…' });
  setPrep(prepAt('collecting'), 'Preparing files…');
  const blockFiles: UploadFilePayload[] = [];
  const emittedFilenames = new Set<string>();
  let totalDuration = 0;
  ready.forEach(({ block, blockIdx }) => {
    const take = block.takes[block.favIdx];
    // Sum the CLIP duration (sliced range), not the full take duration —
    // the timeline progress + duration display reflect actual playback
    // length, not raw file length. For TTS clipDuration === fullDuration
    // since there's no in/out slicing there.
    const meta = block._stsTakeMeta;
    const clipDuration = meta && meta.outPoint != null && meta.inPoint != null
      ? Math.max(0, meta.outPoint - meta.inPoint)
      : (take.duration || 0);
    if (clipDuration > 0) totalDuration += clipDuration;

    const filename = makeBlockFilename(block, blockIdx);
    if (emittedFilenames.has(filename)) return;
    emittedFilenames.add(filename);

    const blob = new Blob([take.buffer], { type: 'audio/mpeg' });
    emit({ kind: 'file-start', name: filename, sizeBytes: blob.size });
    blockFiles.push({ filename, blob });
    emit({ kind: 'file-done', name: filename });
  });

  // ---- Voice-Only WAV (Task #58) ----
  // Slow render — emit file-start before the await so the user sees it
  // appear in "preparing" while the offline render churns.
  const voiceWavName = `${prefix}_TIMELINE-VOICE.wav`;
  emit({
    kind: 'phase',
    phase: 'rendering-voice',
    label: 'Rendering Voice-Only WAV…',
  });
  setPrep(prepAt('rendering-voice'), 'Rendering Voice-Only WAV…');
  emit({ kind: 'message', text: 'Rendering Voice-Only WAV…' });
  emit({ kind: 'file-start', name: voiceWavName });
  checkAbort();
  const voiceWav = await renderVoiceOnlyWav(exportInputWithSignal);
  emit({ kind: 'file-done', name: voiceWavName });

  // ---- PreMixed WAV (FB3#4) ----
  // Only when there's something to mix (video audio OR an SFX bed). Voice-
  // only assets skip this; UploadModal's "include video" checkbox is
  // unrelated and only gates the MP4 below.
  const hasMixSource =
    !!ctx.exportInput.videoSrc ||
    (ctx.exportInput.sfxBuffers?.length ?? 0) > 0;
  let preMixedWav: Blob | null = null;
  let preMixedWavName: string | null = null;
  // The rendered AudioBuffer is kept for the video mux below — it reuses
  // the buffer via `premixedAudio` instead of fetching + decoding the full
  // reference video a second time (OOM on >1GB videos, audit 2026-07-17).
  let preMixedBuffer: AudioBuffer | null = null;
  const wantsVideoUpload = !!ctx.includeVideo && !!ctx.videoBlob;
  let originalVideoAudio: AudioBuffer | null = null;
  // Cloud-Video nur, wenn es auch wirklich die Quelle ist: hat das Talent ein
  // eigenes Video geladen, ist `exportInput.videoSrc` ein Blob.
  const cloudVideoSrc =
    typeof ctx.exportInput.videoSrc === 'string' && ctx.asset.videoPath
      ? ctx.exportInput.videoSrc
      : null;
  const wantsCloudVideo =
    !!cloudVideoSrc &&
    (shouldAutoUploadCloudVideo(ctx.cloudVideoSize === undefined ? 0 : ctx.cloudVideoSize) || !!ctx.includeVideo);
  // Das Cloud-Video, wie es der Premix-Render geladen hat — der Mux liest
  // daraus, statt es ein zweites Mal herunterzuladen (Audit M20).
  let loadedCloudVideo: Blob | null = null;
  if (hasMixSource) {
    preMixedWavName = `${prefix}_TIMELINE-PREMIX.wav`;
    emit({
      kind: 'phase',
      phase: 'rendering-mixed',
      label: 'Rendering PreMixed WAV…',
    });
    setPrep(prepAt('rendering-mixed'), 'Rendering PreMixed WAV…');
    emit({ kind: 'message', text: 'Rendering PreMixed WAV…' });
    emit({ kind: 'file-start', name: preMixedWavName });
    checkAbort();
    preMixedBuffer = await renderMixedAudioBuffer({
      ...exportInputWithSignal,
      // Nur wenn das eigene Video mit hochgeht, wird sein Original-Ton
      // gebraucht (siehe `_VIDEO_ORIGINAL-AUDIO.wav` unten).
      onVideoAudioDecoded: wantsVideoUpload
        ? (buf) => { originalVideoAudio = buf; }
        : undefined,
      // Video-Ton fehlt im Premix (Laden/Dekodieren gescheitert) → sichtbarer
      // Hinweis statt still (Audit M18).
      onWarning: (text) => emit({ kind: 'warning', text }),
      onVideoSourceLoaded: wantsCloudVideo
        ? (blob) => { loadedCloudVideo = blob; }
        : undefined,
    });
    preMixedWav = audioBufferToWav(preMixedBuffer);
    emit({ kind: 'file-done', name: preMixedWavName });
  }

  // ---- script.txt + params.txt ----
  const scriptText = buildScriptTxt({
    assetType: ctx.asset.assetType,
    script: ctx.asset.script,
    blocks: ctx.blocks,
    freeMode: ctx.freeMode,
    isCustom: ctx.asset.isCustom,
  });
  const scriptBlob = new Blob([scriptText], { type: 'text/plain;charset=utf-8' });
  const scriptName = `${prefix}_SKRIPT.txt`;
  emit({ kind: 'file-start', name: scriptName, sizeBytes: scriptBlob.size });
  emit({ kind: 'file-done', name: scriptName });

  // buildParamsTxt iterates ctx.blocks itself (filtering by favIdx) so the
  // emitted block-number matches the position in the original asset block
  // array. The makeFilename callback delegates to the TTS / STS-aware
  // helper above so params.txt rows reference the same filenames the audio
  // payloads were uploaded under.
  const paramsText = buildParamsTxt({
    blocks: ctx.blocks,
    voiceName: ctx.voiceName,
    isSts: ctx.isSts,
    makeFilename: (blockIdx, block) => makeBlockFilename(block, blockIdx),
  });
  const paramsBlob = new Blob([paramsText], { type: 'text/plain;charset=utf-8' });
  const paramsName = `${prefix}_ELEVENLABS-PARAMETERS.txt`;
  emit({ kind: 'file-start', name: paramsName, sizeBytes: paramsBlob.size });
  emit({ kind: 'file-done', name: paramsName });

  // ---- Optional timecodes.csv (only when at least one block has a fav) ----
  // STS gets an extended row shape (clip label + take filename + trim
  // info) so the studio editor can map each master-timeline clip back
  // to which slice of which take file it came from. The take-filename
  // closure mirrors makeBlockFilename so the CSV references the same
  // file the upload payload contains.
  const csvText = buildTimecodesCsv({
    assetTitle: ctx.asset.title,
    blocks: ctx.blocks,
    videoDuration: ctx.videoDuration,
    isSts: ctx.isSts,
    makeStsTakeFilename: ctx.isSts
      ? (meta, block) =>
          buildStsBlockFilename({
            ...filenameCtx,
            session: meta.session,
            takeInSession: meta.takeInSession,
            voiceMarker: computeVoiceMarker(block),
          })
      : undefined,
  });
  let csvBlob: Blob | null = null;
  const csvName = `${prefix}_TIMECODES.csv`;
  if (csvText) {
    // UTF-8 mit BOM, sonst zeigt Excel ar/th/zh/bn/vi als Zeichensalat.
    csvBlob = timecodesCsvBlob(csvText);
    emit({ kind: 'file-start', name: csvName, sizeBytes: csvBlob.size });
    emit({ kind: 'file-done', name: csvName });
  }

  // ---- Optional video copy ----
  // Zwei Quellen führen hierher (Spec 2026-09-21 § 7):
  //   a) eigenes Video des Talents — nur mit gesetzter Checkbox (Blob),
  //   b) Admin-Cloud-Video — automatisch bei jedem Upload, solange es klein
  //      genug ist; darüber entscheidet die vorbelegte Checkbox.
  // In beiden Fällen ist `_VIDEO.mp4` IMMER der Premix (Voice + Video-Ton +
  // SFX-Bett mit Ducking). Scheitert der Mux, gibt es kein stilles Rohvideo
  // unter diesem Namen mehr: eigenes Video → `_VIDEO_RAW.mp4` + Warnung,
  // Cloud-Video (kein lokaler Blob) → gar kein Video + Warnung.
  const videoFiles: UploadFilePayload[] = [];
  let largeVideo: { filename: string; blob: Blob } | null = null;
  const muxSource: string | Blob | null = wantsVideoUpload
    ? ctx.videoBlob!
    : wantsCloudVideo
      ? (loadedCloudVideo ?? cloudVideoSrc)
      : null;
  if (muxSource) {
    // Reuse the same prefix the audio payloads use so the video copy
    // sorts alongside its siblings in WebDAV (and includes _v{N}).
    const videoName = `${prefix}_VIDEO.mp4`;

    // Null, solange kein fertiges MP4 vorliegt — beim Cloud-Video bleibt es
    // im Fehlerfall null (kein lokaler Blob, auf den man zurückfallen könnte).
    let videoBlobToUpload: Blob | null = null;
    let uploadName = videoName;
    /** Warnung fürs Talent, sobald der Mux nicht durchlief. */
    const emitMuxWarning = () => {
      emit({
        kind: 'warning',
        text: wantsVideoUpload
          ? 'The video could not be mixed — the original video was uploaded as _VIDEO_RAW.mp4 instead.'
          : 'The video preview could not be rendered — audio files were uploaded normally.',
      });
    };
    if (hasVideoExportSupport()) {
      try {
        checkAbort();
        emit({ kind: 'phase', phase: 'muxing-video', label: 'Muxing video…' });
        setPrep(prepAt('muxing-video'), 'Muxing video…');
        emit({ kind: 'message', text: 'Muxing video…' });
        // Beim eigenen Video liest der Mux den Blob direkt. Eine blob:-URL darf
        // hier nicht hinein: die Produktions-CSP (`connect-src 'self'`) blockt
        // deren fetch, der Mux scheiterte dadurch seit dem Hetzner-Umzug bei
        // JEDEM selbst geladenen Video und fiel still auf das Rohvideo zurück
        // (Befund 2026-09-21). Beim Cloud-Video ist die Quelle der Blob, den
        // der Premix-Render schon geladen hat; nur ohne ihn (Video-Kanal
        // stumm, Laden gescheitert) die same-origin Stream-URL.
        const muxVideoSrc = muxSource;
        const videoInput: VideoExportInput = {
          videoSrc: muxVideoSrc,
          // Nur Telemetrie/Doku auf diesem Weg: `exportTimelineVideoToBlob`
          // schreibt immer nach OPFS und liest `inputSize` gar nicht — die
          // Größe steuert ausschließlich den Download-Pfad
          // (`exportTimelineVideo`, Save-File-Picker ab
          // VIDEO_EXPORT_STREAM_THRESHOLD). Beim Cloud-Video kommt der Wert
          // aus dem HEAD des Aufrufers, 0 heißt unbekannt.
          inputSize: ctx.videoBlob?.size ?? ctx.cloudVideoSize ?? 0,
          suggestedFilename: videoName,
          audio: exportInputWithSignal,
          premixedAudio: preMixedBuffer ?? undefined,
          signal: ctx.signal,
          // MP4Box demux reports "Reading video… N%" — parse and forward as
          // mux-progress so the modal can show a real determinate bar
          // during the slow demux phase. Other status strings (Encoding AAC,
          // Muxing MP4) don't carry a percent; they fall through to phase
          // labels and the modal stays in indeterminate stripe mode.
          onStatus: (status) => {
            const m = /(\d{1,3})\s*%/.exec(status);
            if (m) {
              const pct = Math.min(100, Math.max(0, Number(m[1])));
              emit({ kind: 'mux-progress', percent: pct });
              setPrep(prepAt('muxing-video', pct / 100));
            }
          },
        };
        videoBlobToUpload = await exportTimelineVideoToBlob(videoInput);
        emit({ kind: 'message', text: 'Video muxed' });
      } catch (err) {
        // User-initiated cancel must NOT fall through to the raw-video
        // fallback — propagate AbortError so the pipeline rejects and the
        // modal closes cleanly. The fallback only exists for codec /
        // browser-support failures.
        if ((err as DOMException)?.name === 'AbortError') throw err;
        // Log the FULL error (object + stack), not just .message — past
        // reports showed the message contained an unrelated path which
        // suggests the real error wraps something deeper. The whole
        // object lands in DevTools so we can drill into it.
        console.warn(
          '[uploadPipeline] video mux failed — own video falls back to _VIDEO_RAW.mp4, cloud video is skipped.',
        );
        console.warn(
          '  → error.name:',
          err instanceof Error ? err.name : '(non-Error)',
        );
        console.warn(
          '  → error.message:',
          err instanceof Error ? err.message : String(err),
        );
        console.warn(
          '  → error.stack:',
          err instanceof Error ? err.stack : '(no stack)',
        );
        console.warn('  → full error object:', err);
        console.warn('  → mux input:', {
          videoSrc: ctx.videoSrc,
          inputSize: ctx.videoBlob?.size ?? ctx.cloudVideoSize ?? 0,
          videoBlobType: ctx.videoBlob?.type ?? '(cloud video)',
          hasExportInput: !!ctx.exportInput,
          totalDuration: ctx.exportInput?.totalDuration,
          voiceClipsCount: ctx.exportInput?.voiceClips?.length,
        });
        // Eigenes Video: das Rohvideo geht unter eigenem Namen mit, damit
        // `_VIDEO.mp4` immer der Premix bleibt. Cloud-Video: kein lokaler
        // Blob → es gibt schlicht kein Video in dieser Version, das Original
        // liegt ohnehin in der Cloud (`_ORIGINAL_VIDEO_PATH.txt`).
        if (wantsVideoUpload && ctx.videoBlob) {
          videoBlobToUpload = ctx.videoBlob;
          uploadName = `${prefix}_VIDEO_RAW.mp4`;
        }
        emitMuxWarning();
      }
    } else {
      // Browser ohne WebCodecs — gleiche Regel wie beim Mux-Fehler.
      console.warn(
        '[uploadPipeline] video export not supported in this browser — no premix MP4',
      );
      if (wantsVideoUpload && ctx.videoBlob) {
        videoBlobToUpload = ctx.videoBlob;
        uploadName = `${prefix}_VIDEO_RAW.mp4`;
      }
      emitMuxWarning();
    }

    // Kein Blob → beim Cloud-Video ist der Mux gescheitert; die Version geht
    // ohne Video raus (Warnung ist schon draußen).
    if (videoBlobToUpload) {
      emit({
        kind: 'file-start',
        name: uploadName,
        sizeBytes: videoBlobToUpload.size,
      });
      // Diagnostic — written to console so we can verify in prod whether
      // a given upload took the bundle path or the single-PUT path. Reads:
      //   "[uploadPipeline] video routing — size=1.83 GB threshold=50 MB → large"
      // (or "→ bundle"). If a 1.8GB video logs "→ bundle" we know the
      // threshold check was somehow bypassed.
      const _routeKB = videoBlobToUpload.size / 1024;
      const _routeSizeStr = _routeKB > 1024 * 1024
        ? `${(_routeKB / 1024 / 1024).toFixed(2)} GB`
        : _routeKB > 1024
          ? `${(_routeKB / 1024).toFixed(1)} MB`
          : `${_routeKB.toFixed(0)} KB`;
      const _isLarge = videoBlobToUpload.size > LARGE_FILE_THRESHOLD;
      debugLog(
        `[uploadPipeline] video routing — size=${_routeSizeStr} (${videoBlobToUpload.size}B) threshold=${(LARGE_FILE_THRESHOLD / 1024 / 1024).toFixed(0)} MB → ${_isLarge ? 'large' : 'bundle'}`,
      );
      // Branch: large videos go as their own streamed PUT AFTER the main
      // bundle uploads (so the version folder exists). Small videos ride
      // along in the multipart bundle.
      if (_isLarge) {
        largeVideo = { filename: uploadName, blob: videoBlobToUpload };
        // Don't emit file-done yet — the PUT happens later, fire it then.
      } else {
        videoFiles.push({ filename: uploadName, blob: videoBlobToUpload });
        emit({ kind: 'file-done', name: uploadName });
      }
    }
  }

  // ---- Original video path (cloud video only) ----
  // Nur wenn das Cloud-Video auch wirklich die Quelle war: hat das Talent ein
  // eigenes Video geladen, ist `exportInput.videoSrc` ein Blob.
  const docFiles: UploadFilePayload[] = [];
  if (ctx.asset.videoPath && typeof ctx.exportInput.videoSrc === 'string') {
    const pathName = `${prefix}_ORIGINAL_VIDEO_PATH.txt`;
    const pathBlob = new Blob([buildOriginalVideoPathTxt(ctx.asset.videoPath)], { type: 'text/plain;charset=utf-8' });
    emit({ kind: 'file-start', name: pathName, sizeBytes: pathBlob.size });
    docFiles.push({ filename: pathName, blob: pathBlob });
    emit({ kind: 'file-done', name: pathName });
  }

  // ---- Original video audio (only alongside an uploaded custom video) ----
  // `_VIDEO.mp4` trägt den Premix; der unveränderte Video-Ton bleibt als WAV
  // daneben erhalten. Der Puffer stammt aus dem Premix-Render oben. Fehlt er
  // (Video ohne dekodierbare Tonspur), gibt es die Datei nicht.
  if (wantsVideoUpload && originalVideoAudio) {
    const originalAudioName = `${prefix}_VIDEO_ORIGINAL-AUDIO.wav`;
    emit({ kind: 'file-start', name: originalAudioName });
    videoFiles.push({ filename: originalAudioName, blob: audioBufferToWav(originalVideoAudio) });
    emit({ kind: 'file-done', name: originalAudioName });
  }

  // ---- Assemble final list ----
  // The LAST item carries isFinal:true (vanilla wire format). Not load-bearing
  // any more: the server reserves the version up front and commits the record
  // once all expected files are there (server/uploadVersions.ts).
  // Build the full file list, then split it: any file > LARGE_FILE_THRESHOLD
  // (50 MB) gets queued for its own PUT via /upload-file. The bundle is
  // everything smaller. This catches LONG-timeline voice WAVs (5 min stereo
  // 48kHz = ~58 MB) + the PreMixed WAV — both individually under 100 MB but
  // together blowing past the bundle's request-body cap. Without this
  // second-pass split the bundle 413'd before the large-file phase started.
  const allFilesPlanned: UploadFilePayload[] = [
    ...blockFiles,
    { filename: voiceWavName, blob: voiceWav },
    ...(preMixedWav && preMixedWavName
      ? [{ filename: preMixedWavName, blob: preMixedWav }]
      : []),
    { filename: scriptName, blob: scriptBlob },
    ...(csvBlob ? [{ filename: csvName, blob: csvBlob }] : []),
    ...docFiles,
    ...videoFiles,
    { filename: paramsName, blob: paramsBlob, isFinal: true },
  ];

  // Pull out any oversized files (≥ 50 MB) for their own PUT, and — Audit
  // 2026-09-24 M14 — keep the bundle SUM under 90 MB (Caddy caps the bundle
  // body at 100 MB): the largest bundle files move to the large-file queue
  // until it fits. The bundle's last file must own the version-record write
  // (isFinal), the large-file PUTs happen after. Rules in lib/uploadSplit.ts.
  const { bundle: allFiles, large: extraLargeFiles } = splitBundle(allFilesPlanned, {
    singleLimit: LARGE_FILE_THRESHOLD,
    bundleLimit: BUNDLE_SUM_LIMIT,
  });

  // Combine the explicit largeVideo split (set up earlier) with any extra
  // large files we just pulled out.
  const largeQueue: { filename: string; blob: Blob }[] = [
    ...(largeVideo ? [largeVideo] : []),
    ...extraLargeFiles,
  ];

  // Diagnostic (debugLog, flag `tro-vs-debug`) — verifies the routing. Logs each file with its
  // size + which path it took (bundle vs large).
  const _bundleTotal = allFiles.reduce((s, f) => s + f.blob.size, 0);
  debugLog(
    `[uploadPipeline] routing (bundle ${allFiles.length} files, ${(_bundleTotal / 1024 / 1024).toFixed(1)} MB · large ${largeQueue.length} files):`,
    {
      bundle: allFiles.map((f) => ({
        name: f.filename,
        mb: +(f.blob.size / 1024 / 1024).toFixed(1),
      })),
      large: largeQueue.map((f) => ({
        name: f.filename,
        mb: +(f.blob.size / 1024 / 1024).toFixed(1),
      })),
    },
  );

  // ---- Multipart POST upload ----
  // fetch can't expose request-body progress, so api.uploadAsset switches
  // to XHR when an `onUploadProgress` callback is supplied. We emit
  // file-bytes events keyed to the synthetic UPLOAD_BUCKET so the modal
  // shows a single "Upload" row with an MB counter.
  //
  // Two-phase reality:
  //   1. XHR bytes-sent — fast on local/HTTP2; the browser flushes the
  //      multipart body into the OS socket buffer almost instantly for
  //      payloads under a few MB.
  //   2. Server processing — the server still has to MKCOL the version
  //      folder + PUT every file to WebDAV before responding. This can
  //      easily take several seconds.
  //
  // We map phase 1 onto 0.20..0.90 of the overall bar (capped at 0.90 so
  // the bar never claims to be done while we're really waiting on phase 2).
  // The first time loaded >= total fires we emit a one-shot
  // `server-processing` event; the modal switches the byte-counter to
  // "Processing on server…" with an indeterminate bar that creeps from
  // 0.90 toward 0.97 over the next few seconds. The terminal `overall:1`
  // only fires after the XHR response actually arrives.
  const totalUploadBytes = allFiles.reduce((sum, f) => sum + f.blob.size, 0);
  // Ab hier sind alle Dateien gebaut: Vorbereitung voll, Bytes bekannt.
  progress.prep = 1;
  progress.bundleBytes = totalUploadBytes;
  progress.largeTotal = largeQueue.reduce((sum, f) => sum + f.blob.size, 0);
  emit({ kind: 'phase', phase: 'uploading', label: 'Uploading audio…' });
  emitOverall('Uploading audio…');
  emit({ kind: 'message', text: 'Uploading audio…' });
  emit({
    kind: 'file-start',
    name: UPLOAD_BUCKET,
    sizeBytes: totalUploadBytes,
  });

  // Note filename is derived from the same prefix as the audio files so the
  // The server stores `{prefix}_NOTE.txt` alongside the version. Skip
  // noteFilename when there's no note — the server only writes the file
  // when `note` is set.
  const noteFilename = ctx.note ? `${prefix}_NOTE.txt` : undefined;
  // Tracks the bytes-sent phase end so we emit `server-processing` exactly
  // once even if XHR fires multiple late progress events.
  let serverPhaseAnnounced = false;
  const serverResult = await uploadAsset(
    ctx.toolId,
    ctx.sessionFolder,
    ctx.assetIdx,
    assetFolderName,
    allFiles,
    totalDuration,
    {
      note: ctx.note ?? null,
      noteFilename,
      userName: ctx.userName,
      assetId: ctx.asset.id,
      // Forward the sub-mode for free/custom uploads. The server writes
      // `version.mode` into KV when present; absent for predefined
      // TTS-only / STS-only assets so legacy records stay lean.
      freeMode: ctx.freeMode,
      // Custom-Asset: Titel für /stream (Ordnername), siehe UploadAssetMeta.
      customTitle: ctx.asset.isCustom ? ctx.asset.title : undefined,
      // Angekündigte große Dateien: die Version bleibt serverseitig
      // `pending`, bis die letzte davon in der Cloud liegt (M15).
      largeFiles: largeQueue.map((f) => f.filename),
    },
    (loaded, total) => {
      const denom = total > 0 ? total : totalUploadBytes;
      // Cap the visible byte-counter so the modal never reports "5 MB / 5 MB"
      // while we're still waiting on the server. We display the smaller of
      // (loaded, denom) so the math always reads as in-progress until the
      // server-processing handoff replaces the line entirely.
      const visibleSent =
        denom > 0 && !serverPhaseAnnounced
          ? Math.min(loaded, Math.max(0, denom - 1))
          : loaded;
      emit({
        kind: 'file-bytes',
        name: UPLOAD_BUCKET,
        sentBytes: visibleSent,
        totalBytes: denom,
      });
      // Bundle-Bytes zählen nur bis 80 % des Bundle-Anteils (uploadProgress.ts):
      // der Dienst schreibt sie erst nach Empfang an die Cloud, ohne Signal.
      progress.bundleSent = Math.max(progress.bundleSent, Math.min(loaded, denom));
      // Once XHR has flushed every byte to the OS, swap to the "Saving to
      // cloud…" stage — der Balken steht ehrlich, bis die Antwort kommt.
      if (!serverPhaseAnnounced && denom > 0 && loaded >= denom) {
        serverPhaseAnnounced = true;
        progress.awaitingServer = true;
        emit({ kind: 'server-processing', name: UPLOAD_BUCKET });
        emit({ kind: 'phase', phase: 'server', label: 'Saving to server…' });
        emitOverall('Saving to server…');
        emit({ kind: 'message', text: 'Saving to server…' });
      } else {
        emitOverall();
      }
    },
    ctx.signal,
  );
  // The server computes the version authoritatively from KV and returns it.
  // Adopt it for everything that happens AFTER the bundle POST — the large-file
  // uploads and the local version record — so oversized files land in the
  // exact folder the server just created and the KV patch targets the record
  // it just wrote, even if a concurrent upload slipped in between the resync
  // above and this POST. Falls back to our resynced number if the server
  // (legacy/unexpected) omits it.
  const committedVersion =
    typeof serverResult?.version === 'number' ? serverResult.version : uploadVersion;
  const uploadId = typeof serverResult?.uploadId === 'string' ? serverResult.uploadId : undefined;
  // Der Server entscheidet über den Asset-Ordner (Ordner der bisherigen
  // Versionen, Integrations-Review 2026-09-24) — große Dateien und Aufräumen folgen ihm.
  const serverFolder = typeof serverResult?.folder === 'string' && serverResult.folder ? serverResult.folder : null;
  const folderChanged = !!serverFolder && serverFolder !== assetFolderName;
  if (serverFolder) assetFolderName = serverFolder;
  if (committedVersion !== uploadVersion || uploadId || folderChanged) {
    ctx.onUploadTarget?.({ version: committedVersion, assetFolderName, ...(uploadId ? { uploadId } : {}) });
  }
  // XHR response arrived → server is genuinely done. If we never hit the
  // bytes-sent terminus (e.g. lengthComputable was false for the whole
  // upload), still announce the server-phase boundary so the modal's
  // bookkeeping stays consistent.
  if (!serverPhaseAnnounced) {
    emit({ kind: 'server-processing', name: UPLOAD_BUCKET });
    emit({ kind: 'phase', phase: 'server', label: 'Saving to server…' });
  }
  emit({ kind: 'file-done', name: UPLOAD_BUCKET });
  // Antwort da: Bundle liegt in der Cloud, der zurückgehaltene Anteil zählt.
  progress.bundleSent = totalUploadBytes;
  progress.bundleSaved = true;
  progress.awaitingServer = false;
  emitOverall();

  // ---- Single PUT for every file > LARGE_FILE_THRESHOLD ----
  // The main bundle has already created the version folder + written the
  // version record (with these names announced as `largeFiles`). Now send
  // each oversized file as one streamed PUT via /upload-file; the server
  // books it in the record so the version flips to `complete` with the
  // last one. Originally this only handled the video; long-timeline voice
  // WAVs (≥58MB stereo for 5min) also route here since the 1.8GB-video
  // tests blew the bundle past 100MB.
  if (largeQueue.length > 0) {
    // Byte-Fortschritt über die SUMME aller großen Dateien (uploadProgress.ts).
    let queueBytesDoneSoFar = 0;
    try {
    for (let qi = 0; qi < largeQueue.length; qi++) {
      const file = largeQueue[qi];
      const sizeMB = (file.blob.size / (1024 * 1024)).toFixed(0);
      const label =
        largeQueue.length > 1
          ? `Uploading ${file.filename} (${sizeMB} MB) — ${qi + 1}/${largeQueue.length}`
          : `Uploading video (${sizeMB} MB)`;
      emit({ kind: 'phase', phase: 'uploading-large-file', label });
      emitOverall(label);
      emit({ kind: 'message', text: label });
      await uploadLargeFile({
        toolId: ctx.toolId,
        version: committedVersion,
        sessionFolder: ctx.sessionFolder,
        assetFolderName,
        assetId: ctx.asset.id,
        filename: file.filename,
        blob: file.blob,
        signal: ctx.signal,
        onProgress: (sentBytes, totalBytes) => {
          emit({ kind: 'file-bytes', name: file.filename, sentBytes, totalBytes });
          // Der Dienst streamt die Datei durch — Client-Bytes sind der echte
          // Fortschritt. Kumulativ über die Warteschlange, nie rückwärts.
          progress.largeSent = Math.max(progress.largeSent, Math.min(queueBytesDoneSoFar + sentBytes, progress.largeTotal));
          // Alle Bytes raus, Antwort (Buchung im Record) steht aus.
          progress.awaitingServer = totalBytes > 0 && sentBytes >= totalBytes;
          emitOverall();
        },
      });
      queueBytesDoneSoFar += file.blob.size;
      progress.largeSent = Math.max(progress.largeSent, Math.min(queueBytesDoneSoFar, progress.largeTotal));
      progress.awaitingServer = false;
      emitOverall();
      emit({ kind: 'file-done', name: file.filename });
    }
    } catch (err) {
      // Large-file upload failed after retries. The bundle POST already wrote
      // the KV version record (committedVersion), so without cleanup the
      // version shows "Uploaded · vN" on reload while the video / big WAV is
      // missing — a false-complete deliverable. Wipe the partial version so
      // a re-upload starts clean. Skip on user-abort (the caller handles
      // cancel cleanup); the cleanup itself is best-effort.
      const isAbort = !!(err && typeof err === 'object' && (err as { name?: string }).name === 'AbortError');
      if (!isAbort) {
        try {
          await cancelUploadVersion(ctx.toolId, {
            sessionFolder: ctx.sessionFolder,
            assetFolderName,
            version: committedVersion,
            assetId: ctx.asset.id,
            uploadId,
          });
        } catch { /* best-effort */ }
      }
      throw err;
    }
  }

  // Ein Abbruch, der erst nach dem letzten Netzwerk-Await eintraf, darf die
  // Version nicht still als Erfolg durchreichen: Das Modal hat dem Talent
  // gerade "files will be deleted" bestätigt, der Aufrufer räumt auf
  // AbortError hin die gemeldete Version auf (Audit M15).
  checkAbort();

  emit({ kind: 'phase', phase: 'done', label: 'Upload complete' });
  progress.done = true;
  emitOverall('Upload complete');
  emit({ kind: 'message', text: 'Upload complete' });

  const resultFiles = allFiles.map((f) => f.filename);
  for (const f of largeQueue) resultFiles.push(f.filename);
  return {
    version: committedVersion,
    files: resultFiles,
    note: ctx.note ?? null,
    duration: totalDuration,
    uploadedAt: new Date().toISOString(),
    // Mirror the server-side `mode` so the local store record matches what
    // KV will return on next session-restore (round-trip parity). Omitted
    // when `freeMode` is undefined to keep predefined-asset records clean.
    ...(ctx.freeMode ? { mode: ctx.freeMode } : {}),
  };
}
